import { ProviderType } from '../../api';
import { emptyRosterAddress, IRosterAddressValues, ROSTER_ADDRESS_TYPES } from '../../services/roster/roster.form';
import { BaseButton } from '../base/button/button';
import { AreaField } from '../base/form/area/area';
import { InputField } from '../base/form/input/input';
import { SelectField } from '../base/form/select/select';
import { US_STATE_OPTIONS } from '../base/select/us-states';
import { Toggle } from '../base/toggle/toggle';
import { ROSTER_PLACE_OF_SERVICE_OPTIONS } from './roster-display';
import { mdiDeleteOutline, mdiPlus } from '@mdi/js';
import Icon from '@mdi/react';
import { Field, FieldArray, useField, useFormikContext } from 'formik';
import React, { useContext } from 'react';
import * as S from './roster-drawer.styles';

/**
 * Whether the drawer shows a record it cannot save — the provider app's API has no create or update
 * route. The fields stay readable but cannot be changed, and adding or removing addresses is not
 * offered; the drawer says why in its footer.
 */
export const RosterReadOnlyContext = React.createContext(false);

const PROVIDER_TYPE_OPTIONS = Object.values(ProviderType).map(type => ({ value: type, label: type }));
const ADDRESS_TYPE_LABELS: Record<string, string> = { MAILING: 'Mailing', PHYSICAL: 'Physical', PAY_TO: 'Pay-to' };
const ADDRESS_TYPE_OPTIONS = ROSTER_ADDRESS_TYPES.map(type => ({
    value: type,
    label: ADDRESS_TYPE_LABELS[type] ?? type,
}));

interface ITextProps {
    name: string;
    label: string;
    placeholder?: string;
    required?: boolean;
    span?: 1 | 2;
    autoComplete?: string;
}

function TextRow({ name, label, placeholder, required, span, autoComplete }: ITextProps): React.ReactElement {
    const readOnly = useContext(RosterReadOnlyContext);
    return (
        <S.Field span={span}>
            <S.Label htmlFor={`roster-${name}`}>
                {label}
                {required && <S.Required aria-hidden={true}>*</S.Required>}
            </S.Label>
            <Field
                id={`roster-${name}`}
                name={name}
                component={InputField}
                placeholder={placeholder}
                autoComplete={autoComplete ?? 'off'}
                disableLatin1Validation={true}
                disabled={readOnly}
            />
        </S.Field>
    );
}

interface ISelectProps {
    name: string;
    label: string;
    options: { value: string; label: string }[];
    required?: boolean;
    span?: 1 | 2;
    placeholder?: string;
}

function SelectRow({ name, label, options, required, span, placeholder }: ISelectProps): React.ReactElement {
    const readOnly = useContext(RosterReadOnlyContext);
    return (
        <S.Field span={span}>
            <S.Label>
                {label}
                {required && <S.Required aria-hidden={true}>*</S.Required>}
            </S.Label>
            <Field
                name={name}
                component={SelectField}
                options={options}
                placeholder={placeholder}
                aria-label={label}
                // `disabled`, not react-select's `isDisabled`: BaseSelect maps the one onto the other
                // and would overwrite an `isDisabled` passed through.
                disabled={readOnly}
            />
        </S.Field>
    );
}

function ToggleRow({ name, label, hint }: { name: string; label: string; hint: string }): React.ReactElement {
    const [field, , helpers] = useField<boolean>(name);
    const readOnly = useContext(RosterReadOnlyContext);
    return (
        <S.ToggleRow>
            <Toggle
                aria-label={label}
                value={!!field.value}
                disabled={readOnly}
                onChange={(value): void => void helpers.setValue(value)}
            />
            <S.ToggleText>
                <S.ToggleLabel>
                    {label} — {field.value ? 'Enabled' : 'Disabled'}
                </S.ToggleLabel>
                <S.Hint>{hint}</S.Hint>
            </S.ToggleText>
        </S.ToggleRow>
    );
}

/** The design's "Provider profile": core metadata and identifiers. */
export function ProviderProfileFields(): React.ReactElement {
    return (
        <S.Section>
            <S.SectionHead>
                <div>
                    <S.SectionTitle>Provider profile</S.SectionTitle>
                    <S.SectionSubtitle>Core provider metadata and identifiers.</S.SectionSubtitle>
                </div>
            </S.SectionHead>
            <S.Grid>
                <SelectRow name="type" label="Provider type" options={PROVIDER_TYPE_OPTIONS} required={true} />
                <TextRow name="organization" label="Organization" placeholder="Organization name" />
                <TextRow name="first" label="First name" placeholder="Individual first name" />
                <TextRow name="last" label="Last name" placeholder="Individual last name" />
                <TextRow name="middle" label="Middle name" placeholder="Individual middle name" />
                <TextRow name="suffix" label="Suffix" placeholder="MD, PhD, BCBA" />
                <TextRow name="npi" label="NPI" placeholder="10-digit NPI" required={true} />
                <TextRow name="ein" label="EIN" placeholder="Organization tax number" />
                <TextRow name="ssn" label="SSN" placeholder="Individual tax number" />
                <TextRow name="ptan" label="PTAN" placeholder="Medicare number" />
                <TextRow name="license" label="License" placeholder="License or certificate number" />
                <TextRow name="taxonomyCode" label="Taxonomy code" placeholder="e.g. 207R00000X" />
                <TextRow name="taxonomyQualifier" label="Taxonomy qualifier" placeholder="Usually ZZ" />
                <TextRow name="email" label="Email" placeholder="Email address of the provider" autoComplete="email" />
                <TextRow name="phone" label="Phone" placeholder="404-555-0123" />
                <TextRow name="fax" label="Fax" placeholder="404-555-0199" />
            </S.Grid>
        </S.Section>
    );
}

/**
 * ENTER-ops controls: Halt. The design's Professionals and Payments automation switches are left
 * out on purpose — the support provider dialog turns both off on every billing save (confirmed
 * intentional, 2026-09-25), and saving here does the same (`rosterProviderDraft`), so a switch here
 * would offer something the next save undoes.
 */
export function ProviderAutomationFields(): React.ReactElement {
    return (
        <S.Section>
            <S.SectionHead>
                <div>
                    <S.SectionTitle>Controls</S.SectionTitle>
                    <S.SectionSubtitle>Internal controls, visible to ENTER ops only.</S.SectionSubtitle>
                </div>
            </S.SectionHead>
            <ToggleRow name="halt" label="Halt" hint="Stop all claim activity for this provider" />
        </S.Section>
    );
}

/** The design's "Facility profile". */
export function FacilityProfileFields(): React.ReactElement {
    const readOnly = useContext(RosterReadOnlyContext);
    return (
        <S.Section>
            <S.SectionHead>
                <div>
                    <S.SectionTitle>Facility profile</S.SectionTitle>
                    <S.SectionSubtitle>Update the facility metadata and identifiers.</S.SectionSubtitle>
                </div>
            </S.SectionHead>
            <S.Grid>
                <TextRow name="name" label="Name" placeholder="Facility name" required={true} span={2} />
                <TextRow name="nickname" label="Nickname" placeholder="Facility nickname" />
                <TextRow name="npi" label="NPI" placeholder="10-digit NPI" />
                <SelectRow
                    name="placeOfServiceCode"
                    label="Place of service"
                    options={ROSTER_PLACE_OF_SERVICE_OPTIONS}
                    placeholder="Choose a place of service"
                />
                <TextRow name="ein" label="EIN" placeholder="Facility EIN" />
                <TextRow name="ssn" label="SSN" placeholder="Facility SSN" />
                <TextRow name="other" label="Other ID" placeholder="Facility other ID" />
                <S.Field span={2}>
                    <S.Label htmlFor="roster-description">Description</S.Label>
                    <Field
                        id="roster-description"
                        name="description"
                        component={AreaField}
                        rows={3}
                        placeholder="Facility description"
                        disableLatin1Validation={true}
                        disabled={readOnly}
                    />
                </S.Field>
            </S.Grid>
        </S.Section>
    );
}

/** Mailing, physical and pay-to addresses, each complete or not at all (see `roster.form.ts`). */
export function AddressFields({ defaultName }: { defaultName: string }): React.ReactElement {
    const { values } = useFormikContext<{ addresses: IRosterAddressValues[] }>();
    const readOnly = useContext(RosterReadOnlyContext);

    return (
        <FieldArray name="addresses">
            {({ push, remove }): React.ReactElement => (
                <S.Section>
                    <S.SectionHead>
                        <div>
                            <S.SectionTitle>Addresses</S.SectionTitle>
                            <S.SectionSubtitle>
                                Mailing and physical addresses used on submitted claims.
                            </S.SectionSubtitle>
                        </div>
                        {!readOnly && (
                            <BaseButton
                                type="button"
                                variant="outlined"
                                size="small"
                                onClick={(): void => push(emptyRosterAddress(defaultName))}
                            >
                                <S.ButtonContent>
                                    <Icon path={mdiPlus} size={0.65} />
                                    Add address
                                </S.ButtonContent>
                            </BaseButton>
                        )}
                    </S.SectionHead>
                    {values.addresses.length === 0 && <S.Empty>No addresses on file.</S.Empty>}
                    {values.addresses.map((_address, index) => (
                        <S.Address key={index}>
                            <S.AddressHead>
                                Address {index + 1}
                                {!readOnly && (
                                    <BaseButton
                                        type="button"
                                        variant="text"
                                        size="small"
                                        onClick={(): void => remove(index)}
                                    >
                                        <S.ButtonContent>
                                            <Icon path={mdiDeleteOutline} size={0.65} />
                                            Remove
                                        </S.ButtonContent>
                                    </BaseButton>
                                )}
                            </S.AddressHead>
                            <S.Grid>
                                <SelectRow
                                    name={`addresses.${index}.type`}
                                    label="Address type"
                                    options={ADDRESS_TYPE_OPTIONS}
                                    required={true}
                                />
                                <TextRow
                                    name={`addresses.${index}.name`}
                                    label="Name"
                                    placeholder="Address name"
                                    required={true}
                                />
                                <TextRow
                                    name={`addresses.${index}.line1`}
                                    label="Line 1"
                                    placeholder="Address line 1"
                                    required={true}
                                    span={2}
                                />
                                <TextRow
                                    name={`addresses.${index}.line2`}
                                    label="Line 2"
                                    placeholder="Address line 2"
                                    span={2}
                                />
                                <TextRow
                                    name={`addresses.${index}.city`}
                                    label="City"
                                    placeholder="City"
                                    required={true}
                                />
                                <SelectRow
                                    name={`addresses.${index}.state`}
                                    label="State"
                                    options={US_STATE_OPTIONS as { value: string; label: string }[]}
                                    placeholder="State"
                                    required={true}
                                />
                                <TextRow
                                    name={`addresses.${index}.postal`}
                                    label="Postal code"
                                    placeholder="5 or 9 digits"
                                    required={true}
                                />
                            </S.Grid>
                        </S.Address>
                    ))}
                </S.Section>
            )}
        </FieldArray>
    );
}
